import { ColDef, ColGroupDef } from 'ag-grid-community';
import {
    DEFAULT_ROW_GROUP_COLUMN,
    LAST_YEAR_COLUMN_STYLE,
    getDateColumnsForVentilation,
    permanentJobFormatter,
    dateVentilatedTablesCellRendererSelector,
    MonthlyColDefGetterArguments,
    isColumnFromLastYearToggle,
    shouldHideColumnByDefault,
    GREY_HEADER_CLASS,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { NUMBER_FILTER } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { DEFAULT_AMOUNT_COLUMN } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { getAgentColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import {
    PERMANENT,
    POSITION_LABEL,
    SALARY_RAW_CHARGED_FIELD,
    ETP_FIELD,
    POSITION_ADMIN,
    HOURS_FIELD,
} from 'routes/HumanResourcesModule/business/definitions';
import { floatFormatter } from 'ui/AgGridReact/formatters';
import { getDateColumnLabel } from 'routes/HumanResourcesModule/helpers/dataHelpers';
import { getGroupedOrgChartHrColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { getExerciceMonthlyPeriodFormatted } from 'routes/HumanResourcesModule/PayState/helpers/dataHelpers';
import { getMantyStatusColumn } from 'routes/HumanResourcesModule/PayState/helpers/colDefinitions';

const getAmountColumns = (
    colDefGetterArgs: MonthlyColDefGetterArguments,
): ColGroupDef[] => {
    const { dateColumns, isMonthly, exercice, showLastYearColumns, t } =
        colDefGetterArgs;
    const dates = getDateColumnsForVentilation(dateColumns, isMonthly);
    // TODO: Quand tout les tableaux utiliserons le nouvelle ordre, il faudra
    // corriger la fonction getDateColumnsForVentilation plutôt que reverse son résultat.
    if (isMonthly) {
        dates.reverse();
    }
    return dates.map((date) => {
        const isLastYearToggleColumn = isColumnFromLastYearToggle(
            date,
            isMonthly,
            exercice,
        );
        const isHidden = shouldHideColumnByDefault(
            showLastYearColumns,
            isLastYearToggleColumn,
        );
        const headerClass = isLastYearToggleColumn
            ? GREY_HEADER_CLASS
            : undefined;
        const cellStyle = isLastYearToggleColumn
            ? LAST_YEAR_COLUMN_STYLE
            : undefined;
        const cellRendererSelector = dateVentilatedTablesCellRendererSelector(
            isMonthly,
            exercice,
            date,
        );

        let headerName = getDateColumnLabel(date, isMonthly);
        if (!isMonthly) {
            headerName += ' ' + getExerciceMonthlyPeriodFormatted(exercice);
        }

        return {
            headerName,
            headerClass,
            children: [
                {
                    ...DEFAULT_AMOUNT_COLUMN,
                    field: `${SALARY_RAW_CHARGED_FIELD}..${date}`,
                    hide: isHidden,
                    headerName: t('humanResources.payState.salaryValue'),
                    headerClass,
                    headerTooltip: t(
                        'humanResources.payState.salaryRawChargedDescription',
                    ),
                    cellRendererSelector,
                    cellRendererParams: { exercice },
                    cellStyle,
                },
                {
                    ...NUMBER_FILTER,
                    field: `${colDefGetterArgs.isForPermanentAgent ? ETP_FIELD : HOURS_FIELD}..${date}`,
                    hide: isHidden,
                    headerName: t(
                        `humanResources.payState.${colDefGetterArgs.isForPermanentAgent ? 'etp' : 'hours'}`,
                    ),
                    headerClass,
                    valueFormatter: floatFormatter,
                    aggFunc: 'sum',
                    cellRendererSelector,
                    cellRendererParams: { exercice },
                    cellStyle,
                    width: 120,
                    ...(colDefGetterArgs.isForPermanentAgent
                        ? {
                              headerTooltip: t(
                                  'humanResources.payState.etpDescription',
                              ),
                          }
                        : {}),
                },
            ],
        };
    });
};

export const getColDefs = (
    colDefGetterArgs: MonthlyColDefGetterArguments,
): (ColDef | ColGroupDef)[] => {
    const { t, shouldDisplayPermanent } = colDefGetterArgs;
    return [
        getGroupedOrgChartHrColumn(),
        getAgentColumn(),
        getMantyStatusColumn(),
        {
            ...DEFAULT_ROW_GROUP_COLUMN,
            headerName: t('humanResources.payState.position'),
            field: POSITION_LABEL,
            hide: true,
            enableRowGroup: true,
        },
        {
            ...DEFAULT_ROW_GROUP_COLUMN,
            headerName: t('humanResources.payState.permanent'),
            field: PERMANENT,
            hide: true,
            enableRowGroup: true,
            suppressColumnsToolPanel: shouldDisplayPermanent,
            valueFormatter: permanentJobFormatter,
        },
        {
            ...DEFAULT_ROW_GROUP_COLUMN,
            headerName: t('humanResources.payState.positionAdmin'),
            field: POSITION_ADMIN,
            hide: true,
            enableRowGroup: true,
        },
        ...getAmountColumns(colDefGetterArgs),
    ];
};
